/**
 * The expiry field of `makerTraits`, and nothing else. The constants mirror
 * `@ailefx/order-sdk`'s `MAKER_TRAITS`, which the SPA cannot import (a private
 * registry): when the SDK's layout moves, this file follows. An unreadable
 * value decodes to `null`, never to a number shown as fact.
 */

/** Bit offset of the uint40 expiry inside the 256-bit traits word. */
export const EXPIRATION_OFFSET = 80n;
/** uint40 mask — the expiry is unix *seconds*, not milliseconds. */
export const EXPIRATION_MASK = (1n << 40n) - 1n;

/** Unix seconds; `0` is the protocol's "no expiry", distinct from `null` (unreadable). */
export function decodeExpiry(makerTraits: string | bigint | null | undefined): number | null {
  if (makerTraits === null || makerTraits === undefined) return null;
  let traits: bigint;
  try {
    traits = typeof makerTraits === "bigint" ? makerTraits : BigInt(String(makerTraits).trim());
  } catch {
    // Not a decimal or hex integer: unknown, not zero.
    return null;
  }
  if (traits < 0n) return null;
  return Number((traits >> EXPIRATION_OFFSET) & EXPIRATION_MASK);
}

/** What an order's expiry means right now, ready to render. */
export interface ExpiryView {
  /** "never" (expiry 0), "unknown" (undecodable), "expired", or "live". */
  kind: "never" | "unknown" | "expired" | "live";
  /** Short cell text: "never expires", "—", "expired", "in 2 h". */
  label: string;
  /** Unix seconds, or null when unknown. 0 means "never". */
  unixSeconds: number | null;
}

/** "in 45 s" / "in 12 min" / "in 3 h" / "in 5 d" — coarse on purpose. */
function untilLabel(seconds: number): string {
  if (seconds < 60) return `in ${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `in ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `in ${hours} h`;
  return `in ${Math.floor(hours / 24)} d`;
}

/** The label is coarse so it needs no per-second re-render; `expiryInstant` gives the exact one. */
export function describeExpiry(
  makerTraits: string | bigint | null | undefined,
  now: number = Date.now(),
): ExpiryView {
  const unixSeconds = decodeExpiry(makerTraits);
  if (unixSeconds === null) return { kind: "unknown", label: "—", unixSeconds: null };
  if (unixSeconds === 0) return { kind: "never", label: "never expires", unixSeconds: 0 };
  const secondsLeft = Math.floor((unixSeconds * 1000 - now) / 1000);
  if (secondsLeft <= 0) return { kind: "expired", label: "expired", unixSeconds };
  return { kind: "live", label: untilLabel(secondsLeft), unixSeconds };
}

/** ISO instant an order expires at, for a `title` tooltip; null when unknown. */
export function expiryInstant(makerTraits: string | bigint | null | undefined): string | null {
  const unixSeconds = decodeExpiry(makerTraits);
  if (unixSeconds === null || unixSeconds === 0) return null;
  return new Date(unixSeconds * 1000).toISOString();
}
